import { computed, ref } from "vue";
import { IFileApiService, IFileUploadService } from "../interfaces";
import {
  AbortFileRequestModel,
  CompleteAddFileRequestModel,
  Events,
  FileModel,
  InitAddFilesRequestModel,
  ResponseModel,
  UploadFileChunkRequestModel,
} from "../models";
import {
  ControlParams,
  EventsType,
  FileLocation,
  InitAddFileItem,
  RoomInfo,
  UploadBatchParams,
  UploadFileItem,
  UploadParams,
} from "../types";
import {
  compressImage,
  generateGuid,
  removeBase64Prefix,
  timeout,
} from "../utils";
import { FileUploadStatusEnum, RoomTypeEnum } from "../enums";

type UploadChunkFunction = (
  chunkBase64: string,
  isLastChunk: boolean,
  chunkIndex: number
) => Promise<ResponseModel<boolean>>;

export class FileUploadService implements IFileUploadService {
  constructor(private api: IFileApiService) {
    this.subscribeEvents();
  }

  /**
   * Текущие активные загрузки файлов
   * @returns {ComputedRef<FileModel[]>} Реактивный список файлов в процессе загрузки
   */
  private activeUploads = ref(new Array<FileModel>());
  /**
   * Текущие активные загрузки файлов
   * @returns {ComputedRef<FileModel[]>} Реактивный список файлов в процессе загрузки
   */
  readonly ActiveUploads = computed(() => this.activeUploads.value);
  /**Событие завершения загрузки файла */
  readonly CompleteEvent: EventsType<FileModel> = new Events<FileModel>();

  /**Подписаться на события */
  private subscribeEvents = () => {
    this.api.CompleteEvent.on(this.onComplete);
  };
  /**
   * Обработчик события успешной загрузки файла
   * @param fileId ID файла
   */
  private onComplete = (fileId: number) => {
    const file = this.getFile(fileId);
    if (file) {
      this.activeUploads.value = this.activeUploads.value.filter(
        (file) => file.FileId !== fileId
      );
      this.CompleteEvent.trigger(file);
    }
  };

  /**Возвращает файл из списка импортируемых, если он там есть */
  private getFile = (fileId: number): FileModel | undefined => {
    return this.activeUploads.value.find((file) => file.FileId === fileId);
  };

  /**
   * Получить размер 1 чанка для файла в MB
   * @param file Файл
   */
  private getChunkSize = (file: File) => {
    return 5 * 1024 * 1024;
  };

  /**
   * * Получить размер файла
   * @param file Файл
   * @returns
   */
  private getFileSize = (file: File) => {
    // Размер чанка
    const chunkSize = this.getChunkSize(file);
    // Размер файла
    let fileSize = 0;
    // Кол-во чанков
    const chunksCount = Math.ceil(file.size / chunkSize);

    // Просчет размера файлов по чанкам
    for (let currentChunk = 0; currentChunk < chunksCount; currentChunk++) {
      const start = currentChunk * chunkSize;
      const end =
        currentChunk === chunksCount
          ? file.size
          : Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      fileSize += blob.size;
    }

    return fileSize;
  };

  /**
   * Создает запрос на инициализацияю загрузки файлов
   * @param params
   * @returns
   */
  private createRequestInitUpload = (
    params: UploadParams | UploadBatchParams,
    files: Array<UploadFileItem>,
    filesSizes: Array<number>
  ): InitAddFilesRequestModel => {
    return new InitAddFilesRequestModel({
      FolderId: params.Location.FolderId,
      TaskId: params.Location?.TaskId,
      QueueTaskId: params.Location?.QueueTaskId,
      ChatId: params.Location?.ChatId,
      Files: files.map((file, index) => ({
        FileName:
          file.File.name.toLowerCase() === "image.png"
            ? generateGuid() + ".png"
            : file.File.name,
        FileSize: filesSizes[index],
        FileId: file.FileId
      })),
    });
  };

  /**Создает список файлов */
  private createFiles = (
    params: UploadParams | UploadBatchParams,
    files: Array<UploadFileItem>,
    initFiles: Array<InitAddFileItem>,
    fileSizes: Array<number>
  ) => {
    return initFiles.map((initFile, index) => {
      return new FileModel({
        File: files[index].File,
        FileId: initFile.FileId,
        Location: params.Location,
        Status: FileUploadStatusEnum.Uploading,
        Progress: 0,
        Url: URL.createObjectURL(files[index].File),
        Name: initFile.Name,
        UploadId: initFile.UploadId,
        Size: fileSizes[index],
        DateCreate: files[index].DateCreate
          ? files[index].DateCreate
          : new Date(),
      });
    });
  };

  /**
   * * Загрузка чанка файла
   * @param data
   * @returns
   */
  private uploadFileChunk(data: UploadFileChunkRequestModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      try {
        const resp = await this.api.uploadChunk(data);
        if (!resp || !resp.ChunkIsSuccess) {
          result.IsSuccess = false;
          result.Value = false;
          console.error("ERROR uploadFileChunk", resp);
          return resolve(result);
        }

        result.Value = true;
        return resolve(result);
      } catch (e: any) {
        console.error("ERROR uploadFileChunk", e);
        result.IsSuccess = false;
        result.Value = false;
        result.ErrorCode = e?.ErrorCode;
        result.ErrorMessage = e?.ErrorMessage;
        return resolve(result);
      }
    });
  }

  /**
   * * Получить информацию о комнате
   * @param fileLocation
   * @returns
   */
  private getRoomInfo = (fileLocation: FileLocation): RoomInfo => {
    if (fileLocation.TaskId)
      return {
        Name: fileLocation.TaskId.toString(),
        Type: RoomTypeEnum.Task,
      };
    if (fileLocation.ChatId)
      return {
        Name: fileLocation.ChatId.toString(),
        Type: RoomTypeEnum.Chat,
      };
  };

  /**
   * * Подтвердить полную загрузку
   * @param fileInfo Файл
   * @returns
   */
  private completeUpload = async (fileInfo: FileModel) => {
    if (fileInfo.Status === FileUploadStatusEnum.Uploading)
      fileInfo.Status = FileUploadStatusEnum.Processing;

    // Если обычный файл
    try {
      // Отправляем запрос на подтверждение завершения загрузки файла
      const resp = await this.api.completeAddFile(
        new CompleteAddFileRequestModel({
          UploadId: fileInfo.UploadId,
          FileId: fileInfo.FileId,
          Room: this.getRoomInfo(fileInfo.Location),
        })
      );
      if (!resp) {
        this.activeUploads.value = this.activeUploads.value.filter(
          (file) => file.FileId !== fileInfo.FileId
        );
        return;
      }
    } catch (e: any) {
      console.error(e);
      this.activeUploads.value = this.activeUploads.value.filter(
        (file) => file.FileId !== fileInfo.FileId
      );
    }
  };

  /**
   * * Загрузить файл по чанкам
   * @param file Файл
   * @param uploadChunk Функция для загрузки чанка
   * @param options Опции
   * @returns
   */
  private uploadChunks = (fileId: number, uploadChunk: UploadChunkFunction) => {
    // Информация о файле
    const fileInfo = this.getFile(fileId);
    if (!fileInfo) {
      throw Error("Не найдена информация о файле");
    }
    // Файл для загрузки
    const file = fileInfo.File;
    // Размер 1 чанка
    const chunkSize = this.getChunkSize(file);
    // Кол-во чанков
    const chunksCount = Math.ceil(file.size / chunkSize);

    let uploadSize = 0;

    fileInfo.IsSendingChunk = true;

    return new Promise<boolean>(async (resolve) => {
      const reader = new FileReader();

      // При завершении чтения текущего чанка
      reader.onloadend = async () => {
        // Поставлено на паузу
        if (
          [FileUploadStatusEnum.Pause, FileUploadStatusEnum.Abort].includes(
            fileInfo.Status
          )
        ) {
          fileInfo.IsSendingChunk = false;
          fileInfo.IsSendingLastChunk = false;
          return resolve(true);
        }
        if (reader.result) {
          // Удаляем префикс и получаем base64 строку
          const base64Chunk = removeBase64Prefix(reader.result.toString());
          // Последний чанк
          const isLast = fileInfo.CurrentChunkIndex === chunksCount - 1;
          fileInfo.IsSendingLastChunk = isLast;
          try {
            console.log(
              `Отправка чанка ${fileInfo.CurrentChunkIndex + 1}`,
              isLast
            );
            // Отправка чанка
            const resp = await uploadChunk(
              base64Chunk,
              isLast,
              fileInfo.CurrentChunkIndex
            );

            // Если отправка завершилась неудачно, прерываем операцию
            if (!resp.IsSuccess) {
              console.error(
                `Ошибка отправки части ${fileInfo.CurrentChunkIndex + 1}`
              );
              fileInfo.IsSendingChunk = false;
              fileInfo.IsSendingLastChunk = false;
              return resolve(false);
            } else {
              fileInfo.Progress =
                ((fileInfo.CurrentChunkIndex + 1) * 100) / chunksCount;
            }
            fileInfo.CurrentChunkIndex++;

            // Если не все части обработаны, продолжаем читать следующую часть
            if (fileInfo.CurrentChunkIndex < chunksCount) {
              loadNextChunk();
            } else {
              // Все части отправлены
              console.log("Все чанки файла успешно загружены", {
                fileSize: fileInfo.Size,
                uploadSize,
                fileInfo,
              });
              fileInfo.IsSendingChunk = false;
              fileInfo.IsSendingLastChunk = false;
              this.completeUpload(fileInfo);
              resolve(true);
            }
          } catch (error: any) {
            console.error(
              `Ошибка обработки части ${fileInfo.CurrentChunkIndex + 1}: ${
                error.message
              }`
            );
            fileInfo.IsSendingChunk = false;
            fileInfo.IsSendingLastChunk = false;
            return resolve(false);
          }
        } else {
          console.error("Ошибка преобразования файла в base64");
          fileInfo.IsSendingChunk = false;
          fileInfo.IsSendingLastChunk = false;
          return resolve(false);
        }
      };

      reader.onerror = () => {
        console.error("Ошибка чтения файла");
        fileInfo.IsSendingChunk = false;
        fileInfo.IsSendingLastChunk = false;
        return resolve(false);
      };

      // Функция для загрузки следующего чанка
      const loadNextChunk = () => {
        const start = fileInfo.CurrentChunkIndex * chunkSize;
        const end =
          fileInfo.CurrentChunkIndex === chunksCount
            ? file.size
            : Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);

        uploadSize += blob.size;

        reader.readAsDataURL(blob);
      };

      loadNextChunk();
    });
  };

  /**
   * * Начать/продолжить загрузку чанков файла
   * @param fileInfo Информация о файле
   */
  private startUploadFileChunks(fileInfo: FileModel) {
    fileInfo.Status = FileUploadStatusEnum.Uploading;
    /**Функция на загрузку чанка конкретного видео */
    const _uploadChunk: UploadChunkFunction = (
      chunkBase64: string,
      _: boolean,
      chunkIndex: number
    ) =>
      this.uploadFileChunk(
        new UploadFileChunkRequestModel({
          FileId: fileInfo.FileId,
          UploadId: fileInfo.UploadId,
          FileChunk: chunkBase64,
          PartNumber: chunkIndex + 1,
        })
      );

    this.uploadChunks(fileInfo.FileId, _uploadChunk);
  }

  /**
   * Сжать файл
   * @param file
   */
  private compressFile = async (
    file: UploadFileItem
  ): Promise<UploadFileItem> => {
    const fileName = file.File.name || "";
    const ext = fileName.split(".")?.pop()?.toLowerCase() ?? "";
    if (
      !file.CompressSize ||
      file.CompressSize < 0 ||
      !["jpg", "jpeg", "png", "webp"].includes(ext)
    )
      return file;

    try {
      return {
        ...file,
        File: await compressImage(file.File, file.CompressSize),
      };
    } catch (e) {
      console.error(e);
      return file;
    }
  };

  /**
   * Сжать файлы
   * @param files
   */
  private compressFiles = (files: Array<UploadFileItem>) => {
    const promises = new Array<Promise<UploadFileItem>>();

    files.forEach((file) => promises.push(this.compressFile(file)));

    return Promise.all(promises);
  };

  /**
   * Загружает один файл
   * @param {UploadParams} params - Параметры загрузки
   * @returns {Promise<ResponseModel<FileModel>>} Модель загруженного файла
   */
  upload(params: UploadParams): Promise<ResponseModel<FileModel>> {
    return new Promise<ResponseModel<FileModel>>(async (resolve) => {
      const result = new ResponseModel<FileModel>();

      // Модифицированные файлы
      const modifyItems = await this.compressFiles([params.Item]);

      // Размеры файлов для загрузки
      const filesSizes = modifyItems.map((file) => this.getFileSize(file.File));

      const request = this.createRequestInitUpload(
        params,
        modifyItems,
        filesSizes
      );

      let response: InitAddFileItem[];

      try {
        // Инициализация загрузки файлов
        response = await this.api.initAddFiles(request);
      } catch (e: any) {
        result.IsSuccess = false;
        result.ErrorCode = e?.ErrorCode;
        result.ErrorMessage = e?.ErrorMessage;
        console.error("ERROR upload", e);
        return resolve(result);
      }

      const files = this.createFiles(params, modifyItems, response, filesSizes);
      this.activeUploads.value.push(...files);
      files.forEach((file) => this.startUploadFileChunks(file));

      result.Value = files[0];

      return resolve(result);
    });
  }

  /**
   * Пакетная загрузка файлов
   * @param {UploadBatchParams} params - Параметры загрузки
   * @returns {Promise<ResponseModel<Array<FileModel>>>} Список загруженных файлов
   */
  uploadBatch(
    params: UploadBatchParams
  ): Promise<ResponseModel<Array<FileModel>>> {
    return new Promise<ResponseModel<Array<FileModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<FileModel>>();

      // Модифицированные файлы
      const modifyItems = await this.compressFiles(params.Items);

      // Размеры файлов для загрузки
      const filesSizes = modifyItems.map((file) => this.getFileSize(file.File));

      const request = this.createRequestInitUpload(
        params,
        modifyItems,
        filesSizes
      );

      let response: InitAddFileItem[];

      try {
        // Инициализация загрузки файлов
        response = await this.api.initAddFiles(request);
      } catch (e: any) {
        result.IsSuccess = false;
        result.ErrorCode = e?.ErrorCode;
        result.ErrorMessage = e?.ErrorMessage;
        console.error("ERROR uploadBatch", e);
        return resolve(result);
      }

      const files = this.createFiles(params, modifyItems, response, filesSizes);
      this.activeUploads.value.push(...files);
      files.forEach((file) => this.startUploadFileChunks(file));

      result.Value = files;

      return resolve(result);
    });
  }
  /**
   * Приостанавливает загрузку файла
   * @param {ControlParams} params - Параметры управления
   */
  pause(params: ControlParams): boolean {
    // Информация о файла
    const fileInfo = this.getFile(params.FileId);
    // Если файл не найден или статус не позволяет поставить видео на паузу
    if (!fileInfo || fileInfo.Status !== FileUploadStatusEnum.Uploading)
      return false;

    // Ставим загрузку на паузу для выхода из функции отправки чанков
    fileInfo.Status = FileUploadStatusEnum.Pause;
    return true;
  }
  /**
   * Возобновляет приостановленную загрузку
   * @param {ControlParams} params - Параметры управления
   */
  resume(params: ControlParams): boolean {
    // Информация о файле
    const fileInfo = this.getFile(params.FileId);
    if (!fileInfo) return false;

    // Начинаем загружать чанки файла
    this.startUploadFileChunks(fileInfo);

    return true;
  }
  /**
   * Отменяет загрузку файла
   * @param {ControlParams} params - Параметры управления
   */
  cancel(params: ControlParams): Promise<ResponseModel> {
    return new Promise<ResponseModel>(async (resolve) => {
      let result = new ResponseModel();

      // Информация о файле
      const fileInfo = this.getFile(params.FileId);

      try {
        // Если файл не найден или его статус не позволяет произвести отмену загрузки
        if (
          !fileInfo ||
          ![
            FileUploadStatusEnum.Pause,
            FileUploadStatusEnum.Uploading,
          ].includes(fileInfo.Status) ||
          fileInfo.IsSendingLastChunk
        ) {
          result.IsSuccess = true;
          result.Value = false;
          return resolve(result);
        }
        // Меняем статус загрузки на отмену
        fileInfo.Status = FileUploadStatusEnum.Abort;

        // Ожидание загрузки последнего чанка
        while (fileInfo.IsSendingChunk) {
          await timeout(200);
        }

        // Удаляем файл из храналища
        this.activeUploads.value = this.activeUploads.value.filter(
          (file) => file.FileId !== params.FileId
        );
        // Отправляем запрос на отмену загрузки файла
        result = await this.api.abortUploadFile(
          new AbortFileRequestModel({
            UploadId: fileInfo.UploadId,
            FileId: fileInfo.FileId,
          })
        );

        result.IsSuccess = result.Value;
        return resolve(result);
      } catch (e: any) {
        console.error("ERROR cancel", e);
        result.Value = false;
        result.IsSuccess = false;
        result.ErrorCode = e.ErrorCode;
        result.ErrorMessage = e.ErrorMessage;
        return resolve(result);
      }
    });
  }
  /**Проверяет, есть ли файл в списке загружаемых */
  isFileInActiveUploads(fileId: number): boolean {
    return this.activeUploads.value.some((file) => file.FileId === fileId);
  }
}
